import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { FormattedMessage as T, useIntl } from "react-intl";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import useGlobalContext from "hooks/globalData";
import LazyTable, {
  TableCellType,
  TableSortType,
  DEFAULT_ROWS_PER_PAGE,
} from "components/LazyTable";
import {
  ResourceLibraryItem,
  IResourceLibraryItemProfession,
  ListResponseOfResourceLibraryItemProfession,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import ResourceLibraryItemProfessionCreateForm from "./ResourceLibraryItemProfessionCreateForm";
import SnackbarMessage from "components/SnackbarMessage";
import { SnackbarMessageProps } from "types/common-types";

interface IResourceLibraryItemProfessionWithId
  extends IResourceLibraryItemProfession {
  id: string;
}

type ResourceLibraryItemProfessionListProps = {
  resourceLibraryItem: ResourceLibraryItem;
};

const useStyles = (theme: Theme) => ({
  rulesHeader: {
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
});

const ResourceLibraryItemProfessionsList = ({
  resourceLibraryItem,
}: ResourceLibraryItemProfessionListProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { resourceLibraryService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("profession.name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfResourceLibraryItemProfession>(
    [
      "resourceLibraryItemProfessions",
      resourceLibraryItem.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      resourceLibraryService.getResourceLibraryItemProfessions(
        resourceLibraryItem.id,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const resourceLibraryItemProfessions = data?.data as IResourceLibraryItemProfessionWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "professionName",
      customSortAccessor: "profession.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "resourceLibraryItemProfessionListNameLabel" }),
    },
  ];

  const onResourceLibraryItemProfessionCreated = () => {
    queryClient.invalidateQueries(["resourceLibraryItemProfessions"]);
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "resourceLibraryItemProfessionCreated" })
      )
    );
  };

  const onDelete = (ids: string[]) => {
    ids.forEach((id) => {
      registerPromise(
        resourceLibraryService.deleteResourceLibraryItemProfession(id)
      )
        .then(() => {
          queryClient.invalidateQueries("resourceLibraryItemProfessions");
        })
        .catch((e) => {
          console.log(e);
        });
    });
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: resourceLibraryItemProfessions ?? [],
          loading: isFetching,
          dense: true,
          titleComponent: (
            <Box sx={styles.rulesHeader}>
              <Typography variant="h6">
                <T id="resourceLibraryItemProfessionListLabel" />
              </Typography>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
                data-cy="addItemProfessionButton"
              >
                <T id="resourceLibraryFormAddItemProfessionButton" />
              </Button>
            </Box>
          ),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onDelete,
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <ResourceLibraryItemProfessionCreateForm
          resourceLibraryItem={resourceLibraryItem}
          onResourceLibraryItemProfessionCreated={
            onResourceLibraryItemProfessionCreated
          }
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default ResourceLibraryItemProfessionsList;
